import React, { useMemo, useState } from 'react';
// @ts-ignore
import styles from 'RequirementsListItem.module.scss';
import RequirementsListItemStatusBadge from './RequirementsListItemStatusBadge';
import LinkButton from '../../../Components/Button/LinkButton';
import { Theme } from '../../../Enums/Theme.enum';
import { RequirementsListItemStatus } from '../Enums/RequirementsListItemStatus.enum';
import Button from '../../../Components/Button/Button';
import RequirementNoteModal from './RequirementNoteModal';
import { prepareDocumentationFileForMediaViewer } from '../../Ecard/Services/eCardFile.service';
import MediaViewerModal from '../../../Components/MediaViewer/MediaViewerModal';
import { useAppContext } from '../../../Services/AppContext';
import {
	DocumentationFile,
	useLazyRequirementFileQuery,
} from '../../../graphql/graphql';
import RemoveConfirmationModal from '../../../Components/RemoveConfirmation/RemoveConfirmationModal';
import Text from '../../../Components/Base/Text/Text';

export interface RequirementsListItemScheme {
	id: string;
	extra: {
		uploaded: boolean;
		approved: boolean;
		signed: boolean;
		signatureRequired: boolean;
		title: string;
		note?: string;
		documentationFileId?: string;
	};
}

interface Props {
	item: RequirementsListItemScheme;
	onNoteEdit: (requirementItemId: string, note: string) => void;
	onApprove: (requirementItemId: string) => void;
}

const RequirementsListItem: React.FC<Props> = ({
	item: {
		id,
		extra: {
			uploaded,
			approved,
			title,
			note,
			documentationFileId,
			signed,
			signatureRequired,
		},
	},
	onNoteEdit,
	onApprove,
}) => {
	const { socketUrl } = useAppContext();
	const [isAddNoteOpen, setIsAddNoteOpen] = useState(false);
	const [fileItem, setFileItem] = useState<DocumentationFile | undefined>(
		undefined,
	);
	const [isApproveConfirmationOpen, setIsApproveConfirmationOpen] =
		useState(false);
	const [getFileData] = useLazyRequirementFileQuery();

	const status: RequirementsListItemStatus = useMemo(() => {
		if (uploaded) {
			if (approved) {
				return RequirementsListItemStatus.APPROVED;
			}
			return RequirementsListItemStatus.WAITING_FOR_APPROVE;
		}
		return RequirementsListItemStatus.NOT_UPLOADED;
	}, [uploaded, approved]);

	const handlePreviewFile = async () => {
		const { data, isLoading } = await getFileData({ id: documentationFileId });
		if (!isLoading) {
			setFileItem(data?.documentationFiles?.data?.[0] || undefined);
		}
	};

	const handleApprove = () => {
		onApprove(id);
		setIsApproveConfirmationOpen(false);
	};

	const handleEditNote = (note: string) => {
		onNoteEdit(id, note);
		setIsAddNoteOpen(false);
	};

	return (
		<div className={styles.item}>
			<div className={styles.leftPart}>
				<div>
					<RequirementsListItemStatusBadge status={status} />
				</div>
				<div>
					<div className={styles.title}>{title}</div>
					{signatureRequired && !signed && (
						<Text
							className={styles.notSigned}
							tx='component.requirementsListItem.notSignedYet'
						/>
					)}
					<div className={styles.note}>{note}</div>
				</div>
			</div>
			<div className={styles.controls}>
				<LinkButton
					firstUpper
					title={note ? 'base.shared.editNote' : 'base.shared.addNote'}
					theme={Theme.BLUE}
					onClick={() => setIsAddNoteOpen(true)}
					className={styles.button}
				/>
				{isAddNoteOpen && (
					<RequirementNoteModal
						requirementId={id}
						note={note}
						isOpen
						onEdit={handleEditNote}
						onClose={() => setIsAddNoteOpen(false)}
					/>
				)}
				{uploaded && documentationFileId && (
					<LinkButton
						onClick={handlePreviewFile}
						title='component.requirementsListItem.preview'
						theme={Theme.BLUE}
						className={styles.button}
					/>
				)}
				{uploaded && !approved && (
					<Button
						theme={Theme.GREEN}
						onClick={() => setIsApproveConfirmationOpen(true)}
						title='component.requirementsListItem.isFine'
					/>
				)}
				{isApproveConfirmationOpen && (
					<RemoveConfirmationModal
						opened
						onClose={() => setIsApproveConfirmationOpen(false)}
						onConfirm={handleApprove}
						confirmationText='component.requirementsListItem.markAsApproveText'
						confirmationTitle='component.requirementsListItem.markAsApproveTitle'
					/>
				)}
				{fileItem && (
					<MediaViewerModal
						opened
						onClose={() => setFileItem(undefined)}
						file={prepareDocumentationFileForMediaViewer(fileItem, socketUrl)}
					/>
				)}
			</div>
		</div>
	);
};

export default RequirementsListItem;
